import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { RootState } from '../../store'
import { Bill, Payment } from '../../types/bills'
import {
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Chip,
    Typography,
    useTheme,
} from '@mui/material'
import { CreditCard, CalendarToday } from '@mui/icons-material'
import { getBillingMonth, getLongFormDate, getShortFormDate } from '../../helpers/stringHelpers'

export const PayNowCard = () => {
    const theme = useTheme()
    const navigate = useNavigate()
    const currentBill = useSelector(
        (state: RootState) => state.billing.selectedBill,
    )
    const bills = useSelector((state: RootState) => state.billing.bills)

    // Helper function to check if a bill has pending payments
    const hasPendingPayments = (bill: Bill) =>
        bill.payments.some((p: Payment) => p.status === 'pending' && !p.is_voided)

    // Calculate total amount due across all unpaid bills (excluding those with pending payments)
    const totalAmountDue = bills?.reduce((total, bill) => {
        // Skip if bill is paid or has no balance due
        if (bill.is_paid || bill.balance_due <= 0) {
            return total
        }

        // Skip if bill has pending payments
        if (hasPendingPayments(bill)) {
            return total
        }

        return total + bill.balance_due
    }, 0) || 0

    // Get the earliest due date among unpaid bills (excluding those with pending payments)
    const earliestDueDate = bills?.reduce((earliest, bill) => {
        // Skip if bill is paid, has no balance due, or has pending payments
        if (bill.is_paid || bill.balance_due <= 0 || hasPendingPayments(bill)) {
            return earliest
        }

        if (!earliest || new Date(bill.due_date) < new Date(earliest)) {
            return bill.due_date
        }
        return earliest
    }, null as string | null)

    return (
        <Card
            sx={{
                mb: 4,
                width: '100%',
                maxWidth: '800px',
                background: '#2a2f35',
                borderRadius: '16px',
                boxShadow: 'none',
                border: '1px solid rgba(255,255,255,0.1)',
                marginRight: 3,
            }}
        >
            <CardHeader
                sx={{
                    '& .MuiCardHeader-title': {
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '100%',
                    },
                }}
                title={
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            width: '100%',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <CreditCard
                                sx={{
                                    mr: 1,
                                    color: theme.palette.primary.main,
                                }}
                            />
                            <Typography
                                variant="h6"
                                sx={{ color: 'rgba(255,255,255,0.8)' }}
                            >
                                Amount Due
                            </Typography>
                        </Box>
                        <Chip
                            icon={<CalendarToday fontSize="small" />}
                            label={`Due ${getShortFormDate(
                                earliestDueDate || currentBill?.due_date,
                            )}`}
                            sx={{
                                bgcolor: 'rgba(255,255,255,0.1)',
                                borderRadius: '20px',
                                border: '1px solid rgba(255,255,255,0.2)',
                                '& .MuiChip-label': {
                                    color: 'rgba(255,255,255,0.8)',
                                },
                                '& .MuiSvgIcon-root': {
                                    color: 'rgba(255,255,255,0.8)',
                                },
                            }}
                        />
                    </Box>
                }
            />
            <CardContent>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <Box>
                        <Typography
                            variant="h4"
                            fontWeight="bold"
                            sx={{ color: 'rgba(255,255,255,0.8)' }}
                        >
                            ${totalAmountDue.toFixed(2)}
                        </Typography>
                        <Typography sx={{ color: 'rgba(255,255,255,0.8)' }}>
                           {getBillingMonth(currentBill?.billing_month || '')}
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        size="large"
                        disabled={totalAmountDue <= 0}
                        onClick={() => navigate('/bill-pay?payAll=true')}
                        sx={{
                            bgcolor: totalAmountDue > 0 ? theme.palette.primary.main : 'rgba(255,255,255,0.3)',
                            color: 'white',
                            borderRadius: '12px',
                            px: 4,
                            '&:hover': {
                                bgcolor: totalAmountDue > 0 ? theme.palette.primary.dark : 'rgba(255,255,255,0.3)',
                            },
                            '&:disabled': {
                                bgcolor: 'rgba(255,255,255,0.3)',
                                color: 'rgba(255,255,255,0.5)',
                            },
                        }}
                    >
                        PAY NOW
                    </Button>
                </Box>
            </CardContent>
        </Card>
    )
}
